import Link from "next/link";
import { Trans, useTranslation } from "next-i18next";
import { Formik } from "formik";
import * as Yup from "yup";
import "yup-phone";
import { Form, FormGroup, Button, Spinner } from "reactstrap";

import routes from "../../../routes";
import { signup, User } from "../../../services/auth";
import { Input } from "../../../components/_atoms";
import { passwordRegex } from "../../../utils";
import withAlerts, {
  AlertTypes,
  AlertDispatch,
} from "../../../components/withAlerts";
import { useRouter } from "next/router";
import { Dispatch, SetStateAction, useEffect } from "react";
import TranslationLinkText from "../../../components/_atoms/TranslationLinkText/TranslationLinkText";
import { useMatomo } from "@jonkoops/matomo-tracker-react";

type FormValues = {
  name: string;
  email: string;
  password: string;
};

type SignupProps = {
  setUser: Dispatch<SetStateAction<User | null>>;
};

type SignupFormProps = SignupProps & { setAlert?: AlertDispatch };

const Signup: React.FC<SignupProps> = (props) => {
  const { t } = useTranslation(["auth", "common"]);

  return (
    <>
      <h2 className="mb-4">{t("signup.title")}</h2>
      <SignupFrom {...props} />
    </>
  );
};

const SignupFrom: React.FC<SignupFormProps> = withAlerts(
  ({ setUser, setAlert }) => {
    const { t } = useTranslation("auth");
    const { locale } = useRouter();

    const validationSchema = Yup.object().shape({
      name: Yup.string().required(t("signup.name.required")),
      password: Yup.string()
        .matches(passwordRegex, t("signup.password.error"))
        .required(t("signup.password.error")),
      email: Yup.string()
        .email(t("signup.email.error"))
        .required(t("signup.email.required")),
      agb: Yup.boolean().oneOf([true], t("signup.agb.required")),
    });

    const { trackEvent } = useMatomo();
    const handleSubmit = async (values: FormValues) => {
      if (values.email && values.password) {
        try {
          const user = await signup(values.email, values.password, {
            ...values,
            locale: locale ?? "de",
          });
          setUser(user);
          trackEvent({ category: "NewUserSignUp", action: "click-event" });
        } catch (error) {
          console.error(error);
          if (error instanceof Error) {
            setAlert &&
              setAlert({
                type: AlertTypes.ERROR,
                key: error.name,
                message: t(`errors.${error.name}`),
              });
          }
        }
      }
    };

    return (
      <>
        <Formik
          className="mb-6"
          onSubmit={handleSubmit}
          initialValues={{
            name: "",
            email: "",
            password: "",
            agb: false,
          }}
          validationSchema={validationSchema}
          validateOnMount
        >
          {(formik) => (
            <Form className="text-start" onSubmit={formik.handleSubmit}>
              <FormGroup className="mb-6">
                <Input
                  label={t("signup.name.label")}
                  name="name"
                  validate={formik}
                  maxLength="256"
                  type="text"
                  aria-label="name"
                  aria-describedby="authNameCaption"
                  required
                />
              </FormGroup>
              <FormGroup className="mb-6">
                <Input
                  label={t("signup.email.label")}
                  name="email"
                  validate={formik}
                  maxLength="256"
                  type="email"
                  required
                />
              </FormGroup>
              <FormGroup className="mb-6">
                <Input
                  label={t("signup.password.label")}
                  name="password"
                  validate={formik}
                  maxLength="256"
                  type="password"
                  aria-label="password"
                  aria-describedby="authPasswordCaption"
                  required
                />
              </FormGroup>
              <FormGroup check inline className="text-start w-100 d-flex">
                <Input
                  label={
                    <Trans
                      i18nKey="signup.agb.label"
                      ns="auth"
                      components={{
                        agbLink: (
                          <TranslationLinkText to={"#"} title="My link1" />
                        ), // TODO: Create AGBs
                        privacyLink: (
                          <TranslationLinkText
                            to={t("imprintLink", { ns: "common" })}
                            title="Data Privacy"
                          />
                        ),
                      }}
                    />
                  }
                  labelClassName="form-check-label"
                  data-testid="agb-checkbox"
                  type="checkbox"
                  className="form-check-input order-first"
                  name="agb"
                  required
                />
              </FormGroup>
              <Button
                color="primary"
                type="submit"
                disabled={!formik.isValid || formik.isSubmitting}
                className="w-100 mb-3"
              >
                {formik.isSubmitting ? (
                  <Spinner color="white">Loading...</Spinner>
                ) : (
                  t("signup.submit")
                )}
              </Button>
            </Form>
          )}
        </Formik>

        <small className="text-muted">
          {t("signinPrompt")}{" "}
          <Link href={routes.signin.path}>{t("signin.title")}</Link>
        </small>
      </>
    );
  },
);

export default Signup;
